<template>
  <Teleport to="body">
    <!-- Top-most layer in the app, deliberately: a box opened from inside a sheet (the camera,
         voice and image overlays at z-[10000], the alarm ring at z-[9999], the server drawer at
         z-[100], the pickers at z-[80]) used to sit *under* it at z-[70]. The click then went to
         that sheet instead of to this button — the button looked dead, and only the next click
         landed, once the sheet had dismissed itself. -->
    <div
      v-if="active"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-4"
      @mousedown.self="onCancel"
      @keydown.esc="onCancel">
      <div class="glass-surface bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md" role="dialog" aria-modal="true">
        <!-- Header -->
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center gap-3">
            <span v-if="active.variant === 'danger'" class="material-symbols-outlined text-red-500 text-2xl"
              >warning</span
            >
            <span v-else-if="active.variant === 'warning'" class="material-symbols-outlined text-yellow-500 text-2xl"
              >warning</span
            >
            <span v-else class="material-symbols-outlined text-blue-500 text-2xl">help</span>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ title }}</h3>
          </div>
        </div>

        <!-- Content -->
        <div class="p-6">
          <p v-if="active.message" class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
            {{ active.message }}
          </p>

          <input
            v-if="active.type === 'prompt'"
            v-model="inputValue"
            :placeholder="active.inputPlaceholder"
            class="mt-3 w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            @keydown.enter="onConfirm" />
        </div>

        <!-- Footer -->
        <div class="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            v-if="active.type !== 'alert'"
            @click="onCancel"
            class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm">
            {{ cancelLabel }}
          </button>
          <button
            @click="onConfirm"
            class="px-4 py-2 text-white rounded-lg transition-colors text-sm"
            :class="confirmButtonClass">
            {{ confirmLabel }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useMessageBoxStore } from '../stores/messageBox';
import type { ActiveMessageBox } from '../stores/messageBox';

const store = useMessageBoxStore();
const { active } = storeToRefs(store);
const { t } = useI18n();

const inputValue = ref('');

// Reset the input each time a new box becomes active
watch(active, (box) => {
  if (box && box.type === 'prompt') {
    inputValue.value = box.defaultValue ?? '';
  }
});

const title = computed(() => {
  const box = active.value;
  if (!box) return '';
  if (box.title) return box.title;
  if (box.type === 'confirm') return t('common.confirm');
  if (box.variant === 'danger' || box.variant === 'warning') return t('common.error');
  return t('common.confirm');
});

const confirmLabel = computed(() => {
  const box = active.value;
  if (!box) return t('common.ok');
  if (box.confirmLabel) return box.confirmLabel;
  if (box.type === 'alert') return t('common.ok');
  if (box.variant === 'danger' || box.variant === 'warning') return t('common.delete');
  return t('common.confirm');
});

const cancelLabel = computed(() => {
  if (active.value?.cancelLabel) return active.value.cancelLabel;
  return t('common.cancel');
});

const confirmButtonClass = computed(() => {
  switch (active.value?.variant) {
    case 'danger':
      return 'bg-red-600 hover:bg-red-700';
    case 'warning':
      return 'bg-orange-600 hover:bg-orange-700';
    default:
      return 'bg-blue-600 hover:bg-blue-700';
  }
});

function onConfirm() {
  const box = active.value as ActiveMessageBox | null;
  if (!box) return;
  if (box.type === 'prompt') {
    store.resolveCurrent(inputValue.value);
  } else {
    store.resolveCurrent(true);
  }
}

function onCancel() {
  const box = active.value as ActiveMessageBox | null;
  if (!box) return;
  if (box.type === 'prompt') {
    store.resolveCurrent(null);
  } else if (box.type === 'confirm') {
    store.resolveCurrent(false);
  } else {
    store.resolveCurrent(undefined);
  }
}
</script>
